import { useLoaderData } from 'react-router-dom';
import { Hero } from './Hero';
import { AboutSummary } from './AboutSummary';
import { ProductsPreview } from './ProductsPreview';
import { ArticlesPreview } from './ArticlesPreview';
import { BannerSection, type BannerDto } from '../../banner';
import type { HeroContentDto } from '../application/dto/HeroContentDto';
import type { MineralDto } from '../../minerals/application/dto/MineralDto';
import type { ArticleDto } from '../../news/application/dto/ArticleDto';
import { useDocumentHead } from '../../../app/seo/useDocumentHead';
import { staticSeo } from '../../../app/seo/static-seo.content';

export interface HomeLoaderData {
  hero: HeroContentDto;
  banner: BannerDto | null;
  aboutIntroduction: string;
  aboutStatistics: { label: string; value: string }[];
  minerals: MineralDto[];
  news: ArticleDto[];
  articles: ArticleDto[];
}

/**
 * Home route entry component. Composes the banner, hero, and a preview of
 * every other public section (products, news, articles, about) into one
 * scrollable page — each section is its own self-contained component so
 * adding/removing/reordering sections later means editing this list, not
 * restructuring the page.
 *
 * All data for every section is resolved by the route's single loader (see
 * app/routes.tsx) via the Composition Root — this component itself has zero
 * data-fetching or business logic, per Clean Architecture rules.
 */
export function HomePage() {
  const { hero, banner, aboutIntroduction, aboutStatistics, minerals, news, articles } =
    useLoaderData() as HomeLoaderData;
  useDocumentHead(staticSeo.home);

  return (
    <>
      <BannerSection banner={banner} />
      <Hero content={hero} />
      <ProductsPreview minerals={minerals} />
      <ArticlesPreview heading="اخبار" basePath="/news" articles={news} />
      <ArticlesPreview heading="مقالات" basePath="/articles" articles={articles} />
      <AboutSummary introduction={aboutIntroduction} statistics={aboutStatistics} />
    </>
  );
}
